import React, { useEffect, useState } from "react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "./ui/dialog";
import { api } from "../lib/api";
import { toast } from "sonner";

export default function OpportunityDialog({ open, onOpenChange, onSaved, initial }) {
    const isEdit = !!initial;
    const [clientName, setClientName] = useState("");
    const [amount, setAmount] = useState("");
    const [status, setStatus] = useState("interesado");
    const [notes, setNotes] = useState("");
    const [arrivalDate, setArrivalDate] = useState("");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open) {
            setClientName(initial?.client_name || "");
            setAmount(initial?.amount ?? "");
            setStatus(initial?.status || "interesado");
            setNotes(initial?.notes || "");
            setArrivalDate(initial?.arrival_date || "");
        }
    }, [open, initial]);

    const submit = async (e) => {
        e.preventDefault();
        if (!clientName.trim()) {
            toast.error("Indica el nombre del cliente");
            return;
        }
        const amt = Number(amount);
        if (!amt || amt < 0) {
            toast.error("Indica un importe válido");
            return;
        }
        setSaving(true);
        try {
            if (isEdit) {
                await api.patch(`/opportunities/${initial.id}`, {
                    client_name: clientName.trim(),
                    amount: amt,
                    notes,
                    arrival_date: arrivalDate || null,
                });
                toast.success("Oportunidad actualizada");
            } else {
                await api.post("/opportunities", {
                    client_name: clientName.trim(),
                    amount: amt,
                    status,
                    notes,
                    arrival_date: arrivalDate || null,
                });
                toast.success("Oportunidad creada");
            }
            onSaved?.();
            onOpenChange(false);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Error al guardar");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="bg-white border border-black/15 rounded-none max-w-md p-0 sm:rounded-none"
                data-testid="opportunity-dialog"
            >
                <form onSubmit={submit}>
                    <DialogHeader className="p-6 border-b border-black/10">
                        <DialogTitle className="font-display text-2xl">
                            {isEdit ? "Editar oportunidad" : "Nueva oportunidad"}
                        </DialogTitle>
                    </DialogHeader>

                    <div className="p-6 space-y-6">
                        <div>
                            <label className="label-eyebrow text-neutral-500 block mb-1">
                                Cliente / Empresa
                            </label>
                            <input
                                data-testid="input-client-name"
                                className="brutal-input"
                                value={clientName}
                                onChange={(e) => setClientName(e.target.value)}
                                placeholder="ACME S.L."
                                autoFocus
                            />
                        </div>
                        <div>
                            <label className="label-eyebrow text-neutral-500 block mb-1">
                                Importe previsto ($)
                            </label>
                            <input
                                data-testid="input-amount"
                                type="number"
                                step="0.01"
                                min="0"
                                className="brutal-input"
                                value={amount}
                                onChange={(e) => setAmount(e.target.value)}
                                placeholder="12500"
                            />
                        </div>
                        {!isEdit && (
                            <div>
                                <label className="label-eyebrow text-neutral-500 block mb-2">
                                    Estado inicial
                                </label>
                                <div className="flex gap-2">
                                    <button
                                        type="button"
                                        data-testid="status-interesado-btn"
                                        onClick={() => setStatus("interesado")}
                                        className={`flex-1 px-3 py-2 text-sm border transition-colors ${
                                            status === "interesado"
                                                ? "bg-black text-white border-black"
                                                : "border-black/20 hover:border-black"
                                        }`}
                                    >
                                        Muy interesados
                                    </button>
                                    <button
                                        type="button"
                                        data-testid="status-posible-btn"
                                        onClick={() => setStatus("posible_cierre")}
                                        className={`flex-1 px-3 py-2 text-sm border transition-colors ${
                                            status === "posible_cierre"
                                                ? "bg-black text-white border-black"
                                                : "border-black/20 hover:border-black"
                                        }`}
                                    >
                                        Cierre 7 días
                                    </button>
                                </div>
                            </div>
                        )}
                        <div>
                            <label className="label-eyebrow text-neutral-500 block mb-1">
                                Fecha de llegada (opcional)
                            </label>
                            <input
                                data-testid="input-arrival-date"
                                type="date"
                                className="brutal-input"
                                value={arrivalDate}
                                onChange={(e) => setArrivalDate(e.target.value)}
                            />
                        </div>
                        <div>
                            <label className="label-eyebrow text-neutral-500 block mb-1">
                                Notas (opcional)
                            </label>
                            <textarea
                                data-testid="input-notes"
                                className="brutal-input resize-none"
                                rows={3}
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                            />
                        </div>
                    </div>

                    <DialogFooter className="p-6 border-t border-black/10 flex !justify-between gap-3">
                        <button
                            type="button"
                            data-testid="dialog-cancel-btn"
                            onClick={() => onOpenChange(false)}
                            className="brutal-btn-outline px-6 py-2 text-sm"
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            disabled={saving}
                            data-testid="dialog-save-btn"
                            className="brutal-btn px-6 py-2 text-sm disabled:opacity-60"
                        >
                            {saving ? "Guardando…" : isEdit ? "Guardar" : "Crear oportunidad"}
                        </button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
